import React from 'react';
import {
    Button,
    Typography,
    Box,
    Grid,
    Chip,
    Divider,
    Avatar
} from '@mui/material';
import GenericModal from '../../components/GenericModal';
import { Person, Email, Phone, LocationOn, Business, CalendarToday } from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { User } from '../../services/userApi';

interface UserViewProps {
    open: boolean;
    user: User | null;
    onClose: () => void;
}

const UserView: React.FC<UserViewProps> = ({ open, user, onClose }) => {
    const { theme } = useTheme();

    if (!user) return null;

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const getStatusChip = (status: number) => {
        return status === 1 ? (
            <Chip
                label="Active"
                color="success"
                size="small"
                variant="outlined"
            />
        ) : (
            <Chip
                label="Inactive"
                color="error"
                size="small"
                variant="outlined"
            />
        );
    };

    return (
        <GenericModal
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                        sx={{
                            bgcolor: theme.palette.secondary.main,
                            width: 48,
                            height: 48
                        }}
                    >
                        {user.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {user.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {user.loginID}
                        </Typography>
                    </Box>
                </Box>
            }
            actions={
                <Button
                    onClick={onClose}
                    sx={{ color: theme.palette.text.secondary }}
                >
                    Close
                </Button>
            }
        >
            <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid size={{ xs: 12 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                        Basic Information
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Person sx={{ color: theme.palette.text.secondary }} />
                        <Typography variant="body2" color="textSecondary">
                            Full Name:
                        </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user.name}
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Email sx={{ color: theme.palette.text.secondary }} />
                        <Typography variant="body2" color="textSecondary">
                            Email Address:
                        </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user.email}
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Business sx={{ color: theme.palette.text.secondary }} />
                        <Typography variant="body2" color="textSecondary">
                            Role:
                        </Typography>
                    </Box>
                    <Chip
                        label={user.type}
                        color={user.type === 'ADMIN' ? 'primary' : 'secondary'}
                        size="small"
                        variant="outlined"
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <CalendarToday sx={{ color: theme.palette.text.secondary }} />
                        <Typography variant="body2" color="textSecondary">
                            Created On:
                        </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDate(user.created_at)}
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Business sx={{ color: theme.palette.text.secondary }} />
                        <Typography variant="body2" color="textSecondary">
                            Status:
                        </Typography>
                    </Box>
                    {getStatusChip(user.status)}
                </Grid>

                {user.branch && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Business sx={{ color: theme.palette.text.secondary }} />
                            <Typography variant="body2" color="textSecondary">
                                Branch:
                            </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {user.branch.name} ({user.branch.code})
                        </Typography>
                    </Grid>
                )}

                {/* Contact Information */}
                {user.profile && (
                    <>
                        <Grid size={{ xs: 12 }}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                                Contact Information
                            </Typography>
                        </Grid>

                        {user.profile.phone && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Phone sx={{ color: theme.palette.text.secondary }} />
                                    <Typography variant="body2" color="textSecondary">
                                        Phone Number:
                                    </Typography>
                                </Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {user.profile.phone}
                                </Typography>
                            </Grid>
                        )}

                        {user.profile.phone2 && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Phone sx={{ color: theme.palette.text.secondary }} />
                                    <Typography variant="body2" color="textSecondary">
                                        Secondary Phone:
                                    </Typography>
                                </Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {user.profile.phone2}
                                </Typography>
                            </Grid>
                        )}

                        {user.profile.cnic && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Person sx={{ color: theme.palette.text.secondary }} />
                                    <Typography variant="body2" color="textSecondary">
                                        CNIC:
                                    </Typography>
                                </Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {user.profile.cnic}
                                </Typography>
                            </Grid>
                        )}

                        {user.profile.city && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <LocationOn sx={{ color: theme.palette.text.secondary }} />
                                    <Typography variant="body2" color="textSecondary">
                                        City:
                                    </Typography>
                                </Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {user.profile.city.name}
                                </Typography>
                            </Grid>
                        )}

                        {user.profile.address && (
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <LocationOn sx={{ color: theme.palette.text.secondary }} />
                                    <Typography variant="body2" color="textSecondary">
                                        Address:
                                    </Typography>
                                </Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {user.profile.address}
                                </Typography>
                            </Grid>
                        )}
                    </>
                )}
            </Grid>
        </GenericModal>
    );
};

export default UserView; 